import { useEffect, useState } from "react";

import { FileWithPath, useDropzone } from "react-dropzone";
import UploadIcon from "@/assets/icons/UploadIcon.svg";
import FileIcon from "@/assets/icons/FileIconFill.svg";
import DeleteIcon from "@/assets/icons/DeleteIconGray.svg";
import LoadingStrip from "@/components/ui/LoadinStrip";
import { FileDetails } from "@/types/types";
import { UploadFileToS3 } from "@/utils/uploadFilestoS3";
import { formatFileSize } from "@/utils/formatFileSize";
import { PlusIcon } from "lucide-react";

interface FileUploadProps {
  videoId?: number;
  onFilesUploaded?: (files: FileDetails[]) => void;
  maxFiles?: number;
  defaultValue?: FileDetails[];
  maxSize?: number; // in bytes
  accept?: { [key: string]: string[] };
  clearFiles?: boolean;
  showSelectedFiles?: boolean;
  useExternalState?: boolean;
  externalFiles?: FileDetails[];
  useLiveUrl?: boolean;
  cardDesignSize?: string;

  setExternalFiles?: React.Dispatch<React.SetStateAction<FileDetails[]>>;
  setVideosAreUpload?: React.Dispatch<React.SetStateAction<string | null>>;
}

const FileUploadFieldPlus: React.FC<FileUploadProps> = ({
  onFilesUploaded,
  maxFiles = 10,
  useLiveUrl = false,
  cardDesignSize = "size-[124px]",

  showSelectedFiles = true,
  defaultValue = [],
  useExternalState = false,
  externalFiles = [],
  setExternalFiles,
  setVideosAreUpload,
  maxSize = 100 * 1024 * 1024, // 100MB default
  accept = {
    "image/jpeg": [],
    "application/pdf": [],
    "video/mp4": [],
    "image/gif": [],
  },
  clearFiles = false,
}) => {
  const [localFiles, setLocalFiles] = useState<FileDetails[]>(defaultValue);
  const [showProgress, setShowProgress] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  const files = useExternalState ? externalFiles : localFiles;
  const setFiles = useExternalState ? setExternalFiles : setLocalFiles;

  const acceptedFileTypes = Object.keys(accept)
    .map((type) => {
      const [mainType, subType] = type.split("/");
      return mainType === "application"
        ? subType.toUpperCase()
        : subType.toUpperCase();
    })
    .join(", ");

  useEffect(() => {
    if (clearFiles && setFiles) {
      setFiles([]);
    }
  }, [clearFiles, setFiles]);

  const onDrop = async (acceptedFiles: FileWithPath[]) => {
    if (uploading || !setFiles) return;
    setUploading(true);

    const newFiles = acceptedFiles.slice(0, maxFiles - files.length);
    const updatedFiles = [...files];

    const uniqueNumber = new Date().getTime();
    const sanitizeFileName = (name: string) =>
      name.replace(/[^a-zA-Z0-9.\-_]/g, "_").slice(0, 100); // safe + short
    for (const file of newFiles) {
      const fileDetails: FileDetails = {
        name: `${uniqueNumber.toString()}-${sanitizeFileName(file.name)}`,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        webkitRelativePath: file.webkitRelativePath,
        local_url: URL.createObjectURL(file),
        live_url: null,
      };

      setFiles((prevFiles) => [...prevFiles, fileDetails]);

      try {
        setShowProgress(fileDetails.name);
        setVideosAreUpload?.(fileDetails.name);
        const uploadedFile = await UploadFileToS3(fileDetails);

        const updatedFile = {
          ...fileDetails,
          live_url: uploadedFile.live_url,
          uploading: false,
        };

        updatedFiles.push(updatedFile);

        setFiles((prevFiles) =>
          prevFiles.map((f) => (f.name === fileDetails.name ? updatedFile : f))
        );

        setVideosAreUpload?.(null);
        setShowProgress(null);
      } catch (error) {
        setShowProgress(null);
        setVideosAreUpload?.(null);
        console.error(`Failed to upload file: ${file.name}`, error);
      }
    }

    setUploading(false);
    onFilesUploaded?.(updatedFiles);
  };

  const handleRemoveFile = (index: number) => {
    if (!setFiles) return;
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onFilesUploaded?.(updatedFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    maxSize,
    accept,
  });

  return (
    <div className="mt-3 flex gap-y-2 gap-x-4 items-start flex-wrap">
      {/* uploaded files */}
      {showSelectedFiles && (
        <>
          {files.map((file, index) => (
            <div
              key={index}
              className="relative bg-[#F8F8F8] border border-[#EAECF0] rounded-[8px] overflow-hidden flex justify-center items-center"
              style={{
                width: "124px",
                height: "124px",
              }}
            >
              <img
                src={
                  !file.live_url
                    ? file.local_url
                    : useLiveUrl
                    ? (file.live_url as string)
                    : (file.local_url as string)
                }
                alt={file.name}
                className="object-cover w-full h-full"
              />
              <button
                className="absolute top-2 right-2 opacity-0 hover:opacity-100 transition-opacity"
                onClick={() => handleRemoveFile(index)}
              >
                <DeleteIcon />
              </button>
            </div>
          ))}
        </>
      )}

      {/* upload design */}
      {(files.length < maxFiles || !showSelectedFiles) && (
        <div
          {...getRootProps()}
          className={`bg-[#F8F8F8] relative size-[124px] border border-dashed border-[#D0D5DD] rounded-[8px] text-center py-3 flex flex-col justify-center items-center cursor-pointer ${cardDesignSize}`}
        >
          <input
            {...getInputProps()}
            className="w-full absolute top-0 left-0 h-full opacity-0"
          />
          <PlusIcon className="text-[#D0D5DD] h-[28px]" />
        </div>
      )}
    </div>
  );
};

export default FileUploadFieldPlus;
